import Vue from "vue";

export default Vue.extend({
  name: "SvgDiagram",
  props: {
    /** SVG data URI or inline SVG. */
    svgData: {
      type: String,
      required: true
    },
    /** Current zoom level (1 = 100%). */
    zoomLevel: {
      type: Number,
      default: 1
    },
    /** ItemNumber whose matching hotspot (`<g hsid="..." name="ItemNumber">`) should be highlighted. */
    highlightedItemNumber: {
      type: [String, Number],
      default: null
    },
    /**
     * ItemNumbers that have a corresponding part in the results list. Hotspots
     * whose `name` isn't in this list are rendered grey and show a
     * "not serviceable" popup instead of highlighting on click. When omitted,
     * every hotspot is treated as serviceable.
     */
    validItemNumbers: {
      type: Array,
      default: null
    },
    /** When false, the diagram is only centered so the caller's `zoomLevel` is preserved. */
    fitOnLoad: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    isDragging: false,
    dragStartX: 0,
    dragStartY: 0,
    dragStartTranslateX: 0,
    dragStartTranslateY: 0,
    translateX: 0,
    translateY: 0,
    /** Disables the transform transition while a zoom level change is being measured for focusHotspot(). */
    suppressTransition: false,
    showUnserviceablePopup: false,
    unserviceablePopupPosition: { x: 0, y: 0 },
    unserviceablePopupTimeoutId: null as any,
  }),
  computed: {
    effectiveZoom(): number {
      return this.zoomLevel * 0.75;
    },
    /** Computed transform style for pan functionality. */
    containerStyle(): any {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.effectiveZoom})`,
        transformOrigin: "top left",
        cursor: this.isDragging ? "grabbing" : "grab",
        transition: (this.isDragging || this.suppressTransition) ? "none" : "transform 0.2s ease-out"
      };
    }
  },
  watch: {
    svgData() {
      this.$nextTick(() => {
        this.applyInitialView();
        this.applyHotspotStates();
      });
    },
    highlightedItemNumber() {
      this.$nextTick(this.applyHotspotStates);
    },
    validItemNumbers() {
      this.$nextTick(this.applyHotspotStates);
    }
  },
  mounted() {
    const content = this.$refs.content as HTMLElement | undefined;
    if (content) {
      content.addEventListener("click", this.onContentClick);
    }
    this.$nextTick(() => {
      this.applyInitialView();
      this.applyHotspotStates();
    });
  },
  beforeDestroy() {
    const content = this.$refs.content as HTMLElement | undefined;
    if (content) {
      content.removeEventListener("click", this.onContentClick);
    }
    document.removeEventListener("mousemove", this.onDrag as any);
    document.removeEventListener("mouseup", this.endDrag as any);
    if (this.unserviceablePopupTimeoutId) {
      clearTimeout(this.unserviceablePopupTimeoutId);
    }
  },
  methods: {
    /** Returns the viewport element used to size and center the diagram. */
    getViewport(): HTMLElement | undefined {
      return this.$el as HTMLElement | undefined;
    },

    /** Returns the transform container element that owns the diagram size. */
    getContainer(): HTMLElement | undefined {
      return this.$refs.container as HTMLElement | undefined;
    },

    /** Centers the diagram within the viewport using the current zoom level. */
    centerDiagram() {
      const viewport = this.getViewport();
      const container = this.getContainer();
      if (!viewport || !container) return;

      const viewportWidth = viewport.clientWidth;
      const viewportHeight = viewport.clientHeight;
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      this.translateX = (viewportWidth - containerWidth * this.effectiveZoom) / 2;
      this.translateY = (viewportHeight - containerHeight * this.effectiveZoom) / 2;
    },

    /**
     * Scales the diagram so it fills the viewer's height (without overflowing its
     * width) and centers it. Most diagrams are portrait while the viewer is not,
     * so the height is the constraining dimension. Emits `zoom-change` so the
     * parent-owned `zoomLevel` (and its zoom % readout) stays in sync.
     */
    fitToViewport() {
      const viewport = this.getViewport();
      const container = this.getContainer();
      if (!viewport || !container) return;

      const viewportWidth = viewport.clientWidth;
      const viewportHeight = viewport.clientHeight;
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      if (!containerWidth || !containerHeight || !viewportHeight) return;

      const scale = Math.min(viewportHeight / containerHeight, viewportWidth / containerWidth);

      this.translateX = (viewportWidth - containerWidth * scale) / 2;
      this.translateY = (viewportHeight - containerHeight * scale) / 2;

      this.$emit("zoom-change", scale / 0.75);
    },

    /** Fits the diagram to the viewer, or centers it at the caller's zoom when fitOnLoad is off. */
    applyInitialView() {
      if (this.fitOnLoad) {
        this.fitToViewport();
        return;
      }
      this.centerDiagram();
    },

    /** Zooms while keeping the point under the cursor fixed in the viewport. */
    zoomToPoint(nextZoomLevel: number, clientX: number, clientY: number) {
      const viewport = this.getViewport();
      const container = this.getContainer();
      if (!viewport || !container) return;

      const viewportRect = viewport.getBoundingClientRect();
      const currentScale = this.effectiveZoom;
      const nextScale = nextZoomLevel * 0.75;

      const cursorX = clientX - viewportRect.left;
      const cursorY = clientY - viewportRect.top;

      const contentX = (cursorX - this.translateX) / currentScale;
      const contentY = (cursorY - this.translateY) / currentScale;

      const nextTranslateX = cursorX - contentX * nextScale;
      const nextTranslateY = cursorY - contentY * nextScale;

      // Preserve the cursor anchor unless doing so would move the diagram past
      // a viewport edge. A smaller diagram stays fully inside the viewport; a
      // larger one continues to cover it without exposing empty space.
      this.translateX = this.constrainTranslation(
        nextTranslateX,
        container.offsetWidth * nextScale,
        viewportRect.width
      );
      this.translateY = this.constrainTranslation(
        nextTranslateY,
        container.offsetHeight * nextScale,
        viewportRect.height
      );
    },

    /** Returns the nearest translation that keeps scaled content within its viewport axis. */
    constrainTranslation(translation: number, scaledContentSize: number, viewportSize: number): number {
      const minTranslation = Math.min(0, viewportSize - scaledContentSize);
      const maxTranslation = Math.max(0, viewportSize - scaledContentSize);
      return Math.min(maxTranslation, Math.max(minTranslation, translation));
    },

    /** Start panning when user clicks and drags on the diagram. */
    startDrag(event: MouseEvent) {
      if (event.button !== 0) return; // Only left-click
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartTranslateX = this.translateX;
      this.dragStartTranslateY = this.translateY;

      document.addEventListener("mousemove", this.onDrag as any);
      document.addEventListener("mouseup", this.endDrag as any);
    },

    /** Handle panning as user drags. */
    onDrag(event: MouseEvent) {
      if (!this.isDragging) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      this.translateX = this.dragStartTranslateX + deltaX;
      this.translateY = this.dragStartTranslateY + deltaY;
    },

    /** Stop panning. */
    endDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.onDrag as any);
      document.removeEventListener("mouseup", this.endDrag as any);
    },

    /** Reset pan position to a centered default view. */
    resetPan() {
      this.$nextTick(() => {
        this.applyInitialView();
      });
    },

    /**
     * Delegated click handler for hotspot elements. SmartEquip diagrams render
     * each callout as `<g hsid="..." name="ItemNumber"><rect/><text/></g>` — the
     * `name` attribute matches a part's ItemNumber. Hotspots with no matching
     * part (see validItemNumbers) show a "not serviceable" popup instead.
     */
    onContentClick(event: MouseEvent) {
      const target = event.target as HTMLElement;
      const hotspot = target.closest("[hsid]") as HTMLElement | null;
      if (!hotspot) return;

      const itemNumber = hotspot.getAttribute("name");
      if (!itemNumber) return;

      if (!this.isItemNumberServiceable(itemNumber)) {
        this.flashUnserviceablePopup(hotspot);
        return;
      }

      this.$emit("hotspot-click", itemNumber);
    },

    /** True when validItemNumbers wasn't provided, or when it contains the given ItemNumber. */
    isItemNumberServiceable(itemNumber: string): boolean {
      if (!Array.isArray(this.validItemNumbers)) return true;
      return this.validItemNumbers.some((n: any) => String(n) === itemNumber);
    },

    /** Briefly shows the "not serviceable" popup, anchored just above the given hotspot. */
    flashUnserviceablePopup(hotspot: HTMLElement) {
      const viewport = this.$el as HTMLElement;
      const hotspotRect = hotspot.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();

      this.unserviceablePopupPosition = {
        x: hotspotRect.left + hotspotRect.width / 2 - viewportRect.left,
        y: hotspotRect.top - viewportRect.top
      };

      if (this.unserviceablePopupTimeoutId) {
        clearTimeout(this.unserviceablePopupTimeoutId);
      }
      this.showUnserviceablePopup = true;
      this.unserviceablePopupTimeoutId = setTimeout(() => {
        this.showUnserviceablePopup = false;
        this.unserviceablePopupTimeoutId = null;
      }, 3000);
    },

    /** Toggles the highlight/unserviceable classes on each hotspot based on highlightedItemNumber and validItemNumbers. */
    applyHotspotStates() {
      const content = this.$refs.content as HTMLElement | undefined;
      if (!content) return;

      const hotspots = content.querySelectorAll("[hsid]");
      hotspots.forEach((el) => {
        const name = el.getAttribute("name");
        const isServiceable = name != null && this.isItemNumberServiceable(name);
        const isHighlighted =
          isServiceable &&
          this.highlightedItemNumber != null &&
          name === String(this.highlightedItemNumber);

        el.classList.toggle("svg-diagram__hotspot--unserviceable", !isServiceable);
        el.classList.toggle("svg-diagram__hotspot--highlighted", isHighlighted);
      });
    },

    /** Pans so the hotspot matching the given ItemNumber is centered in the viewport. */
    async focusHotspot(itemNumber: string | number) {
      const content = this.$refs.content as HTMLElement | undefined;
      const viewport = this.$el as HTMLElement;
      if (!content || !viewport) return;

      const hotspot = Array.from(content.querySelectorAll("[hsid]"))
        .find((el) => el.getAttribute("name") === String(itemNumber)) as HTMLElement | undefined;
      if (!hotspot) return;

      // Suppress the transition so the zoom level change and this measurement
      // both reflect the same, un-animated layout.
      this.suppressTransition = true;
      await this.$nextTick();

      const hotspotRect = hotspot.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();

      // translateX/Y are viewport-space pixels because translation is applied
      // after scaling. Dividing this screen-space delta by the zoom causes the
      // hotspot to overshoot at low zoom and undershoot at high zoom.
      const deltaX = viewportRect.left + viewportRect.width / 2 - (hotspotRect.left + hotspotRect.width / 2);
      const deltaY = viewportRect.top + viewportRect.height / 2 - (hotspotRect.top + hotspotRect.height / 2);

      // Re-enable the transition so the pan itself animates smoothly.
      this.suppressTransition = false;
      this.translateX += deltaX;
      this.translateY += deltaY;
    }
  }
});

